import ks from "../ReUsableTS/kBrowserShared";
import { kUIText } from "./kUIText";
import { kEdTblSelectionMode, kEditableTableActive, kHTMLEditableColType } from "./kEditableTableTypes";
import { kDateInput, kSelect } from "../ReUsableTS/kHTML";
import { kHTMLMultiSelect } from "../ReUsableTS/kHTMLMultiSelect";
import type { kEditableTableCell } from "./kEditableTableCell";
import type { kEditableTableRow } from "./kEditableTableRow";

// Member bodies of kEditableTableCell moved here verbatim (this -> Self) by the size split of 2026-10-20; every kEditableTableCell member keeps its signature and delegates here.
export class kEditableTableCellInput {
    public static _MouseOver(Self: kEditableTableCell, Event:MouseEvent): void {
        if (ks.KErrorMode === true) { return; }
        try {
            if (Self.ParentTable) {
                if (Event.buttons === 1) {
                    Self.ParentTable.Selection.SelectionChangeMulti(Self);
                }
             }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableCellInput._MouseOver");
            return;
        }
    }

    // True when a left mousedown on THIS cell must leave an already-open editor
    // alone instead of committing/closing it: an in-place, non-Shift click or drag
    // inside the cell being text-edited (contentEditable) — a Shift+click instead
    // falls through to exit edit mode and extend the CELL range — OR a click inside
    // its open dropdown / date-colour picker / HTML popup. Keeping the inline text
    // editor lets the browser own the caret / selection (click-to-reposition,
    // drag-to-select) instead of the exit path collapsing it and re-selecting all
    // (the "can't sub-select cell text" bug).
    public static _MouseDownKeepsOpenEditor(Self: kEditableTableCell, EditCell: kEditableTableCell, ShiftHeld: boolean): boolean {
        if (ks.KErrorMode === true) { return false; }
        try {
            if (EditCell.UniqueID !== Self.UniqueID) { return false; }
            if (EditCell.Cell.contentEditable === "true" && ShiftHeld === false) { return true; }
            if (EditCell.ChoiceMultiDD !== null || EditCell.ChoiceDD !== null
                || EditCell.DatePicker !== null || EditCell.HtmlPopup !== null) { return true; }
            return false;
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableCellInput._MouseDownKeepsOpenEditor");
            return false;
        }
    }

    public static _MouseDown(Self: kEditableTableCell, _Event: MouseEvent): void {
        if (ks.KErrorMode === true) { return; }
        try {
            if (Self.ParentTable) {
                kEditableTableActive.SetActiveTable(Self.ParentTable);
            }
            // Only reset selection and exit edit mode for left-clicks. Right-clicks
            // (button 2) must leave the existing selection intact for context menus.
            if (_Event.button !== 0) { return; }
            if (Self.ParentTable && Self.ParentTable.CurrenEditableCell) {
                const EditCell: kEditableTableCell = Self.ParentTable.CurrenEditableCell;
                if (Self["_MouseDownKeepsOpenEditor"](EditCell, _Event.shiftKey) === true) {
                    // A plain second click / drag on the text cell also enters ACTIVE
                    // edit mode (arrows move the caret, Shift+arrow selects text).
                    if (EditCell.Cell.contentEditable === "true" && _Event.shiftKey === false) {
                        Self.ParentTable._InlineEditActive = true;
                    }
                    return;
                }
                EditCell.SetCellReadMode();
                Self.ParentTable.CurrenEditableCell = null;
            }
            // Set the drag anchor to this cell so SelectionChangeMulti has the correct start point
            if (Self.ParentTable) {
                Self.ParentTable.Selection.RowIndexStart = Self.RowIndex;
                Self.ParentTable.Selection.RowIndexEnd = Self.RowIndex;
                Self.ParentTable.Selection.ColindexStart = Self.ColumnIndex;
                Self.ParentTable.Selection.ColIndexEnd = Self.ColumnIndex;
                Self.ParentTable.Selection._AnchorRow = Self.RowIndex;
                Self.ParentTable.Selection._AnchorCol = Self.ColumnIndex;
            }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableCellInput._MouseDown");
        }
    }

    // In-place click on the cell already in inline text-edit mode: keep the
    // browser's native caret / text selection (placed on mousedown) instead of
    // re-running SetSingleCell -> _EditTextOrNumber, which would re-select-all and
    // collapse a click-to-reposition or drag-to-select gesture. Companion guard
    // to _MouseDown, which leaves the mousedown itself alone.
    // Returns true when it consumed the click — CellClick then does nothing more.
    public static _ConsumeInPlaceEditClick(Self: kEditableTableCell, Event?:MouseEvent): boolean {
        if (ks.KErrorMode === true) { return false; }
        try {
            // A plain second click on the cell being edited is an active-edit gesture
            // (keep the native caret). A Shift+click falls through so it can extend a
            // cell range, matching the _MouseDown companion guard.
            if (Self.ParentTable === null || Self.ParentTable.CurrenEditableCell === null
                || Self.ParentTable.CurrenEditableCell.UniqueID !== Self.UniqueID
                || Self.Cell.contentEditable !== "true"
                || (Event !== undefined && Event.shiftKey !== false)) { return false; }
            Self.ParentTable._InlineEditActive = true;
            if (Event) { Event.stopPropagation(); }
            return true;
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableCellInput._ConsumeInPlaceEditClick");
            return false;
        }
    }

    // The non-Shift (single-cell) half of CellClick: select, notify the host, and
    // — unless the row is read-only — open the column's editor. Returns FALSE when
    // CellClick must bail out WITHOUT cancelling the browser default, which is what
    // the read-only-row early return did while this was still inline.
    public static _HandlePrimaryClick(Self: kEditableTableCell, Event?:MouseEvent): boolean {
        if (ks.KErrorMode === true) { return false; }
        try {
            if (Self.ParentTable === null) { return true; }
            Self.ParentTable.Selection.SetSingleCell(Self);
            if(Self.ParentTable.RowClickCB && Self.RowIndex >= 0){
                const ClickedRow = Self.ParentTable.Rows.Items[Self.RowIndex];
                if(ClickedRow){ Self.ParentTable.RowClickCB(ClickedRow); }
            }
            const _ParentRow: kEditableTableRow | undefined = Self.ParentTable.Rows.Items[Self.RowIndex];
            if (_ParentRow && _ParentRow.ReadOnly) { return false; }
            if (Self.ColDef && Self.ColDef.Locked === false) {
                if (Self.ColDef.CustomEditor !== null) {
                    Self.ColDef.CustomEditor(Self);
                } else {
                    Self["_BeginEditForType"](Event);
                }
            }
            return true;
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableCellInput._HandlePrimaryClick");
            return false;
        }
    }

    /** Moved from kEditableTableCell._BeginEditForType (size split); the class member delegates here. */
    public static _BeginEditForType(Self: kEditableTableCell, Event?:MouseEvent): void {
        if (ks.KErrorMode === true) { return; }
        try {
            switch (Self.ColDef.Type) {
                case kHTMLEditableColType.Color:      Self["_EditColor"]();         break;
                case kHTMLEditableColType.Select:     Self["_EditSelect"]();        break;
                case kHTMLEditableColType.SelectMulti:
                    if (Event) { Event.stopPropagation(); }
                    Self["_EditSelectMulti"]();
                    break;
                case kHTMLEditableColType.Text:
                case kHTMLEditableColType.Number:     Self["_EditTextOrNumber"]();  break;
                case kHTMLEditableColType.Currency:   Self["_EditCurrency"]();      break;
                case kHTMLEditableColType.Radio:
                    Self.SetNewValue(Self.CurrentValue !== true);
                    Self.Cell.style.textAlign = "center";
                    break;
                case kHTMLEditableColType.Date:       Self["_EditDate"]();          break;
                case kHTMLEditableColType.RichHtml:   Self["_EditRichHtml"]();      break;
                case kHTMLEditableColType.Checkbox:
                    Self.SetNewValue(Self.CurrentValue !== true);
                    Self.Cell.style.textAlign = "center";
                    Self["_PlayCheckboxAnim"]();
                    break;
                default:
                    break;
            }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableCellInput._BeginEditForType");
        }
    }

    // Swap the cell's read-mode content for an inline editor WITHOUT letting the
    // editor's own intrinsic size change the column width or row height. This grid
    // is an auto-layout table, so a replaced control - a <select> with its dropdown
    // chrome, a native colour/date <input> with its own minimum box - would
    // otherwise GROW the column/row (chrome bigger than the text) or, when the
    // edited cell was the widest, SHRINK it, redrawing with a visible flicker the
    // instant the cell is clicked (root-caused across Select/Colour/Date 2026-07-04;
    // the Colour column's native swatch has a ~44px min-width that pushed a narrow
    // column wider). The fix, measured against every alternative (see the layout
    // probe): pin the cell's min-width/height to its pre-edit box so it cannot
    // collapse, then position the editor ABSOLUTELY filling the cell so it is out of
    // flow and cannot grow anything. Column/row then hold within ~1px. The pin is
    // released on exit in SetNewValue / SetCellReadMode / _StartAddNewChoice. Call
    // this AFTER reading anything off the cell's read-mode DOM (e.g. textContent) -
    // it clears the cell.
    public static _MountInlineEditor(Self: kEditableTableCell, Editor: HTMLElement): void {
        if (ks.KErrorMode === true) { return; }
        try {
            const Rect: DOMRect = Self.Cell.getBoundingClientRect();
            Self.Cell.innerHTML = "";
            Self.Cell.style.minWidth = Math.round(Rect.width) + "px";
            Self.Cell.style.minHeight = Math.round(Rect.height) + "px";
            // Inline `!important` (not plain assignment): a host page's aggressive global CSS
            // - e.g. SharePoint core15.css styling every <select>/<input>, often with
            // `!important` - can override a plain inline size and pull the editor back INTO
            // flow, so its intrinsic box resizes the cell on click. Inline-important beats any
            // external stylesheet, keeping the editor out of flow + cell-filling. (Runtime-
            // confirmed 2026-07-06 on the Massnahmen grid inside a SharePoint form.)
            Editor.style.setProperty("position", "absolute", "important");
            Editor.style.setProperty("left", "0", "important");
            Editor.style.setProperty("top", "0", "important");
            Editor.style.setProperty("width", "100%", "important");
            Editor.style.setProperty("height", "100%", "important");
            Editor.style.setProperty("margin", "0", "important");
            Editor.style.setProperty("box-sizing", "border-box", "important");
            Self.Cell.appendChild(Editor);
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableCellInput._MountInlineEditor");
        }
    }

    public static _EditColor(Self: kEditableTableCell): void {
        if (ks.KErrorMode === true) { return; }
        try {
            Self.ColorPicker = document.createElement("input");
            Self.ColorPicker.type = "color";
            Self.ColorPicker.value = ks.Nz(Self.CurrentValue);
            Self["_MountInlineEditor"](Self.ColorPicker);
            // As in _EditSelect: focus() can run a foreign `change` handler that ends this edit.
            const Picker: HTMLInputElement = Self.ColorPicker;
            Picker.focus();
            if (Self.ColorPicker !== Picker) { return; }
            Picker.click();
            Picker.addEventListener("click", Self.ColorChange);
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableCellInput._EditColor");
        }
    }

    public static _EditSelect(Self: kEditableTableCell): void {
        if (ks.KErrorMode === true) { return; }
        try {
            Self.ChoiceDD = new kSelect();
            if (Self.ChoiceDD && Self.ChoiceDD.Select && Self.ColDef) {
                Self["_FillSelectEditor"](Self.ChoiceDD, Self.ColDef);
                // SetSelectedItem reads the cell's read-mode text, so it must run
                // BEFORE _MountInlineEditor clears the cell. The mount pins the
                // cell box and positions the <select> absolutely so entering edit
                // never resizes the column - see _MountInlineEditor.
                Self.ChoiceDD.SetSelectedItem(ks.Nz(Self.Cell.textContent));
                Self["_MountInlineEditor"](Self.ChoiceDD.Select);
                // focus() is NOT inert: it blurs whatever had the focus, and that control's own
                // `change` handler runs synchronously - a consumer that rebuilds or harvests the
                // grid there (CommitActiveEdit -> SetCellReadMode) has already put this cell back
                // in read mode and cleared ChoiceDD by the time focus() returns. So the editor is
                // held locally and the rest runs only while it is still this cell's editor
                // (DocumentBuilder PX-117: Tab out of a field beside the grid -> "Cannot read
                // properties of null (reading 'Select')").
                const Editor: kSelect = Self.ChoiceDD;
                Editor.Select.focus();
                if (Self.ChoiceDD !== Editor) { return; }
                Editor.Select.click();
                if (Self.ChoiceDD !== Editor) { return; }
                Editor.ChangeCB = Self.ChoiceDDChanged;
            }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableCellInput._EditSelect");
        }
    }

    public static _EditSelectMulti(Self: kEditableTableCell): void {
        if (ks.KErrorMode === true) { return; }
        try {
            Self.ChoiceMultiDD = new kHTMLMultiSelect();
            if (Self.ChoiceMultiDD && Self.ColDef) {
                if (Self.ColDef.MultiSelectOptions) {
                    Self.ChoiceMultiDD.ChoiceOptions = Self.ColDef.MultiSelectOptions;
                } else {
                    Self.ChoiceMultiDD.Choices = Self.ColDef.Options;
                }
                if (Self.ColDef.MultiSelectNoneSelectedOptionText !== "") {
                    Self.ChoiceMultiDD.NoneSelectedOptionText = Self.ColDef.MultiSelectNoneSelectedOptionText;
                }
                if (Self.ColDef.AllowAddNewItem === true) {
                    Self.ChoiceMultiDD.AllowAddNew = true;
                    Self.ChoiceMultiDD.OnAddNewRequested = Self["_RequestNewMultiChoice"];
                }
                // Read the value from CurrentValue, not textContent: the read-mode
                // cell now renders chips, whose concatenated text drops the "; "
                // separators this parse relies on.
                const CurrValue: string = ks.Nz(Self.CurrentValue);
                // Float the multi-select control over the cell (absolute + pinned):
                // its 34px-min collapsed box and inline-expanding checklist would
                // otherwise grow the row and push rows below down. min-height:34px
                // overrides the helper's height:100%, so the control is not clipped.
                const MultiChoiceDiv: HTMLDivElement = Self.ChoiceMultiDD.ToDiv();
                Self["_MountInlineEditor"](MultiChoiceDiv);
                Self.ChoiceMultiDD.OKClickCB = Self["_CompleteMultiSelectEdit"];
                Self.ChoiceMultiDD.SetSelectedChoices(CurrValue.split("; "));
                Self.ChoiceMultiDD.ExpandListWrapper();
            }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableCellInput._EditSelectMulti");
        }
    }

    /** Moved from kEditableTableCell._EnterInlineEdit (size split); the class member delegates here. */
    public static _EnterInlineEdit(Self: kEditableTableCell, Editable: boolean, Seed: (() => string) | null, Before: (() => void) | null, Collapse: boolean, ArmGuard: boolean, Label: string): void {
        if (ks.KErrorMode === true) { return; }
        try {
            if (Before !== null) { Before(); }
            if (Editable) { Self.Cell.contentEditable = "true"; }
            if (Seed !== null) { Self.Cell.textContent = Seed(); }
            Self.Cell.focus();
            const Range: Range = document.createRange();
            Range.selectNodeContents(Self.Cell);
            if (Collapse) { Range.collapse(false); }
            const Sel: Selection | null = window.getSelection();
            if (Sel) { Sel.removeAllRanges(); Sel.addRange(Range); }
            if (ArmGuard) { Self.Cell.addEventListener("beforeinput", Self["_TextEnterPrevent"]); }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableCellInput._EnterInlineEdit");
        }
    }

    public static _EditTextOrNumber(Self: kEditableTableCell): void {
        if (ks.KErrorMode === true) { return; }
        try {
            // A Number cell shows "12 345" (kUIText.Count): editing starts from the raw number, so Number(textContent) parses it back.
            if (Self.ColDef.Type === kHTMLEditableColType.Number) {
                Self["_EnterInlineEdit"](true, () => String(ks.N0(Self.CurrentValue)), null, false, true, "kEditableTableCell._EditTextOrNumber");
                return;
            }
            Self["_EnterInlineEdit"](true, null, null, false, true, "kEditableTableCell._EditTextOrNumber");
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableCellInput._EditTextOrNumber");
        }
    }

    // Excel-style "type-to-edit" entry: enter edit mode on a Text/Number/Currency
    // cell that is merely SELECTED (not yet editing) and seed it with the pressed
    // character, replacing any prior content (type-over). Called by
    // kEditableTable._TryTypeToEdit when a printable key lands on a selected cell.
    // Without this, a cell that is blue-selected but not in contentEditable mode
    // (after a range/drag selection, or a focus reset by the host page) silently
    // swallows every keystroke — the "can't type into the selected cell" bug.
    public static BeginTextEditWithChar(Self: kEditableTableCell, Ch: string): void {
        if (ks.KErrorMode === true) { return; }
        try {
            Self["_EnterInlineEdit"](true, () => Ch, () => {
                // Typing to start editing enters ACTIVE edit mode (caret keys act on
                // the text) — see kEditableTable._InlineEditActive.
                if (Self.ParentTable !== null) { Self.ParentTable._InlineEditActive = true; }
            }, true, true, "kEditableTableCell.BeginTextEditWithChar");
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableCellInput.BeginTextEditWithChar");
        }
    }

    // Collapse the inline text selection to a caret at the end of the cell text —
    // Excel's F2 behaviour (switch a ready cell into active edit with the caret at
    // the end rather than all-selected).
    public static PlaceCaretAtEnd(Self: kEditableTableCell): void {
        if (ks.KErrorMode === true) { return; }
        try {
            Self["_EnterInlineEdit"](false, null, null, true, false, "kEditableTableCell.PlaceCaretAtEnd");
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableCellInput.PlaceCaretAtEnd");
        }
    }

    // Non-destructive value sync for a cell that is ACTIVELY being inline-edited,
    // used by GetGridContentsTokObjectArray so serialising the grid for a write-
    // back never blurs the caret / flips contentEditable off on the cell the user
    // is typing into. Only mirrors CurrentValue from the live DOM text (so IsEmpty
    // keeps the row and _ConvertCellValue serialises the fresh value); deliberately
    // does NOT SetDirty (which would re-arm the 300ms debounce into a self-firing
    // harvest loop and spam the undo stack), does NOT rewrite Cell.textContent
    // (which would collapse the caret), and does NOT grow a trailing row (row
    // growth belongs to the real commit on blur / Tab / Flush, via _CompleteTextEdit).
    public static SyncInlineValueForHarvest(Self: kEditableTableCell): void {
        if (ks.KErrorMode === true) { return; }
        try {
            switch (Self.ColDef.Type) {
                case kHTMLEditableColType.Number:
                    Self.CurrentValue = Number(Self.Cell.textContent);
                    break;
                case kHTMLEditableColType.Currency:
                    Self.CurrentValue = ks.NumberFromCurrency(ks.Nz(Self.Cell.textContent));
                    break;
                default:
                    Self.CurrentValue = ks.Nz(Self.Cell.textContent);
                    break;
            }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableCellInput.SyncInlineValueForHarvest");
        }
    }

    public static _EditCurrency(Self: kEditableTableCell): void {
        if (ks.KErrorMode === true) { return; }
        try {
            Self["_EnterInlineEdit"](true, () => ks.NumberFromCurrency(Self.CurrentValue).toString(), null, false, true, "kEditableTableCell._EditCurrency");
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableCellInput._EditCurrency");
        }
    }

    public static _EditRichHtml(Self: kEditableTableCell): void {
        if (ks.KErrorMode === true) { return; }
        try {
            if (Self.HtmlPopup !== null) { return; } // Popup already open on this cell
            if (Self.ParentTable) {
                Self.ParentTable.OpenRichHtmlEditor(Self);
            }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableCellInput._EditRichHtml");
        }
    }

    public static _EditDate(Self: kEditableTableCell): void {
        if (ks.KErrorMode === true) { return; }
        try {
            Self.DatePicker = new kDateInput();
            Self.DatePicker.Create();
            if (Self.DatePicker && Self.DatePicker.Input) {
                Self.DatePicker.SetBykDate(Self.OrgValue);
                // Absolute-mount so the native date input's chrome never grows the
                // column/row (previously width:clientWidth-10 drifted ~2px) - see
                // _MountInlineEditor.
                Self["_MountInlineEditor"](Self.DatePicker.Input);
                Self.DatePicker.Input.focus();
            }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableCellInput._EditDate");
        }
    }

    public static _CellRightClick(Self: kEditableTableCell, Event:MouseEvent): void {
        if (ks.KErrorMode === true) { return; }
        try {
            if(Self.ParentTable && Self.ParentTable.CellsContextMenu){
                Self.ParentTable.RightClickCell = Self;
                switch (Self.ParentTable.Selection.SelectionMode) {
                    case kEdTblSelectionMode.SingleCell:
                    case kEdTblSelectionMode.NoSelection:
                        // Single cell: only the filter grids surface the menu here
                        // (so "Filter by this value" is reachable without first
                        // making a range). Non-filter grids keep the prior
                        // behaviour of suppressing the range-only menu.
                        if (Self.ParentTable.ShowColumnFilters === true || Self.ParentTable.CellMenuOnSingleCell === true) {
                            Self.ParentTable._UpdateFillMenuVisibility();
                            Self.ParentTable.CellsContextMenu.ShowMenu(Event);
                        } else {
                            Event.preventDefault();
                            Event.stopPropagation();
                        }
                        break;
                    default:
                        //Range Selected
                        Self.ParentTable._UpdateFillMenuVisibility();
                        Self.ParentTable.CellsContextMenu.ShowMenu(Event);
                        break;
                }
            }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableCellInput._CellRightClick");
            return;
        }
    }
}
